/**
 * CetusEd Admin Console — Admin Login Page
 *
 * COMPLIANCE: Admin login requires email, password, AND MFA code (TOTP or
 *   hardware key). This satisfies NIST SP 800-63B AAL3 requirements for
 *   privileged access to systems containing student PII.
 *
 *   Session tokens expire after 4 hours. Idle sessions are terminated after
 *   30 minutes of inactivity.
 */

import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminAuthContext } from './AdminAuthProvider';

export const LoginPage: React.FC = () => {
  const auth = useContext(AdminAuthContext);
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!auth) return null;
  if (auth.isAuthenticated) {
    navigate('/', { replace: true });
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await auth.login(email, password, mfaCode);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed. Please check your credentials and MFA code.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="admin-login-page">
      <div className="admin-login-card">
        <h1>CetusEd Admin Console</h1>
        <p className="login-description">
          Vendor-hosted platform management. MFA is required.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="form-field">
            <label htmlFor="admin-email">Email</label>
            <input
              id="admin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="username"
            />
          </div>

          <div className="form-field">
            <label htmlFor="admin-password">Password</label>
            <input
              id="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </div>

          <div className="form-field">
            <label htmlFor="admin-mfa">MFA Code</label>
            <input
              id="admin-mfa"
              type="text"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value)}
              required
              autoComplete="one-time-code"
              inputMode="numeric"
              maxLength={6}
              placeholder="000000"
            />
          </div>

          {error && <p className="login-error" role="alert">{error}</p>}

          <button className="admin-btn admin-btn--primary admin-btn--full" type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Verifying...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
};
